import {
  createLearnCardOperationId,
  type LearnCardAction,
  type LearnCardActionReceipt,
  type LearnCardActionSnapshot,
  type LearnCardExitDirection,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/** A saved action between two history frames: what undo rolls back and redo replays. */
export type LearnDeckHistoryEdge = {
  /** The way the card left for the next frame; Undo slides it back in from there. */
  direction: LearnCardExitDirection;
  /**
   * The operation id of the redo in progress, minted when that redo starts and kept until it
   * lands. Every retry of the SAME redo reuses it, so a timed-out attempt that lands late and the
   * retry resolve to one review row. Cleared once the redo lands, so the next redo (after another
   * undo) gets a fresh id: reusing an old one would find its soft-deleted row and silently no-op,
   * because the review dedupe deliberately counts undone rows as landed.
   */
  pendingRedoOperationId: string | null;
  receipt: LearnCardActionReceipt;
  snapshot: LearnCardActionSnapshot;
};

/**
 * Replay a history edge's action (history Forward), exactly once across retries, bounded by
 * `deadlineMs`. Every kind of action is replayed, because undo reverses every kind: a lesson is
 * enrolled again, a slide completed again. A timeout leaves `pendingRedoOperationId` in place for
 * the retry; success stores the new receipt (which undo will roll back) and clears it.
 *
 * A replayed lesson acceptance the free-tier gate refuses (it has presented the paywall) rejects
 * with {@link LearnDeckRedoBlockedError}: the forward card was reached by an enrollment that no
 * longer happens, so history must not step onto it.
 */
export async function redoLearnDeckHistoryEdge({
  deadlineMs,
  edge,
  persist,
}: {
  deadlineMs: number;
  edge: LearnDeckHistoryEdge;
  persist: (args: {
    action: LearnCardAction;
    isRedo: true;
    operationId: string;
    snapshot: LearnCardActionSnapshot;
  }) => Promise<LearnCardActionReceipt>;
}): Promise<void> {
  const operationId = (edge.pendingRedoOperationId ??= createLearnCardOperationId());
  const receipt = await withDeadline(
    { deadline: deadlineMs, operation: "Learn deck history redo" },
    () =>
      persist({ action: edge.receipt.action, isRedo: true, operationId, snapshot: edge.snapshot }),
  );
  edge.pendingRedoOperationId = null;
  if (receipt.persisted.kind === "enrollment" && !receipt.persisted.wasAdded) {
    throw new LearnDeckRedoBlockedError();
  }
  edge.receipt = receipt;
}

export class LearnDeckRedoBlockedError extends Error {
  constructor() {
    super("This character can no longer be added to study, so Redo cannot continue past it.");
    this.name = "LearnDeckRedoBlockedError";
  }
}
